import { useEffect } from "react";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import { ExpansionPanel } from "../../../common";
import { ActionPropsProps } from "../ActionProps";
import { Workspace, Meeting, Domain } from "../../../lib/cnb/api/public";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";
import { alpha } from "@mui/material/styles";

interface BookingSettingsProps extends ActionPropsProps {
  currentWorkspace?: Workspace;
  currentDomain?: Domain;
  meetings?: Meeting[];
}

const useStyles = makeStyles()((theme) => ({
  noMeetingsWarning: {
    backgroundColor: alpha(theme.palette.warning.main, 0.3),
    borderColor: alpha(theme.palette.warning.main, 0.4),
  },
}));

const BookingSettings = ({
  formal,
  pathPrefix,
  currentWorkspace,
  currentDomain,
  meetings,
}: BookingSettingsProps) => {
  const { classes, cx } = useStyles();
  // Auto-set the workspace name property
  useEffect(() => {
    if (currentWorkspace) {
      const currentValue = formal.getValue<string>(
        `${pathPrefix}properties.booking-workspace-name`,
      );
      if (currentValue !== currentWorkspace.slug) {
        formal.setValue(
          `${pathPrefix}properties.booking-workspace-name`,
          currentWorkspace.slug,
        );
      }
    }
  }, [currentWorkspace, formal, pathPrefix]);

  if (!currentDomain) return null;
  if (!currentWorkspace) {
    return (
      <Typography className={cx(classes.noMeetingsWarning)}>
        You do not have any workspaces yet. Create a{" "}
        <a
          target={"_blank"}
          href={`${process.env.REACT_APP_CHAT_URL}/wizard`}
          rel="noreferrer"
        >
          new workspace
        </a>{" "}
        via the wizard.
      </Typography>
    );
  }

  if (meetings?.length === 0) {
    return (
      <Typography className={cx(classes.noMeetingsWarning)}>
        You do not have any meetings yet. Create a{" "}
        <a
          target={"_blank"}
          href={`${process.env.REACT_APP_CHAT_URL}/workspace/${currentWorkspace.id}/domain/${currentDomain.id}/meetings/new`}
          rel="noreferrer"
        >
          new booking
        </a>{" "}
        via the dedicated app.
      </Typography>
    );
  }

  return (
    <ExpansionPanel title={"Booking settings"}>
      <FormGroup>
        <TextField
          label="Workspace"
          value={currentWorkspace.name}
          disabled
          InputProps={{
            readOnly: true,
          }}
        />
        <TextField
          label="Meeting"
          select
          SelectProps={{
            native: true,
          }}
          {...formal.getTextFieldProps(
            `${pathPrefix}properties.booking-meeting-id`,
          )}
        >
          <option value="">Select a meeting</option>
          {meetings?.map((meeting) => (
            <option key={meeting.id} value={meeting.niceName}>
              {meeting.niceName}
            </option>
          ))}
        </TextField>
        <input
          type="hidden"
          {...formal.getTextFieldProps(
            `${pathPrefix}properties.booking-hide-content`,
          )}
          value="1"
        />
      </FormGroup>
    </ExpansionPanel>
  );
};

export default BookingSettings;
